import FullScreenLoading from "@/components/FullScreenLoading";
import GenericErrorTemplate from "@/components/GenericErrorTemplate";
import { gql } from "@/gql";
import type { UiFeatureFlagNames } from "@/gql/graphql";
import { pageLayout } from "@/theme/pageLayout";
import { Box } from "@mui/material";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAccount } from "@/context/account";
import { FeatureFlagContext } from "./FeatureFlagContext";
import type { FeatureFlagProviderContext } from "./FeatureFlagContext";

export const ENABLED_FEATURE_FLAGS = gql(`
  query EnabledFeatureFlags($accountId: ID!) {
    enabledFeatureFlags(accountId: $accountId)
  }
`);

const CAN_USE_FEATURE_FLAG = gql(`
  query CanUseFeatureFlag($accountId: ID!, $flagName: UIFeatureFlagNames!) {
    canUseFeature(accountId: $accountId, flagName: $flagName)
  }
`);

const EMPTY_FEATURE_FLAGS: UiFeatureFlagNames[] = [];

type FeatureFlagProviderProps = {
  children?: React.ReactNode;
};

export const FeatureFlagProvider = (props: FeatureFlagProviderProps) => {
  const { children } = props;
  const { t } = useTranslation();

  const { account } = useAccount();
  const accountId = account.id;

  const { data, loading, error, dataState } = useQuery(ENABLED_FEATURE_FLAGS, {
    variables: {
      accountId,
    },

    // Snapshot restore on account switch can repopulate cache for a visited
    // account; cache-and-network keeps instant UI while refetching fresh flags.
    fetchPolicy: "cache-and-network",
    context: { skipGlobalError: true },
  });

  const enabledFeatureFlags: UiFeatureFlagNames[] =
    data?.enabledFeatureFlags ?? EMPTY_FEATURE_FLAGS;

  const [canUseFeatureFlagQuery] = useLazyQuery(CAN_USE_FEATURE_FLAG);

  // Keep the context identity stable so flag consumers do not re-render on
  // unrelated provider updates.
  const value = useMemo((): FeatureFlagProviderContext => {
    return {
      enabledFeatureFlags,
      features: Object.fromEntries(
        enabledFeatureFlags.map((flag) => [flag, true])
      ),
      canUseFeatureFlag: (flagName: UiFeatureFlagNames) => {
        return enabledFeatureFlags.includes(flagName);
      },
      canUseFeatureFlagAsync: async (flagName: UiFeatureFlagNames) => {
        const { data: flagData } = await canUseFeatureFlagQuery({
          variables: {
            accountId,
            // Client-preset enum omits flags that are not referenced in documents.
            flagName,
          },
        });
        return flagData?.canUseFeature ?? false;
      },
    };
  }, [accountId, canUseFeatureFlagQuery, enabledFeatureFlags]);

  if (loading && dataState === "empty") {
    return <FullScreenLoading title={t("feature_flags.loading")} />;
  }

  if (error && dataState === "empty") {
    return (
      <Box sx={pageLayout}>
        <GenericErrorTemplate
          header={t("something_went_wrong")}
          description={t("feature_flags.error_loading")}
          showDealerDashboardButton={false}
        />
      </Box>
    );
  }

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};
